import { PaymentTypes } from 'appConstants';
import { format, parse } from 'date-fns';
import { PackagePromo } from 'subscriptionOptions/types';

export type ISubscription = {
  schoolAccountPackagesId: number;
  subjectName: string;
  subjectsId: number;
  currentPackagesId: number;
  renewalPackagesId: number | null;
  currentPackageLength: string | null;
  renewalPackageLength: string | null;
  currentGeneralPackageName: string | null;
  renewalGeneralPackageName: string | null;
  currentPackageFullName: string | null;
  renewalPackageFullName: string | null;
  currentPackageGrossDiscountedPrice: number | null;
  renewalPackageNetUndiscountedPrice: number | null;
  renewalPackageNetDiscountedPrice: number | null;
  currentPackagePercentOff: number | null;
  renewalPackagePercentOff: number | null;
  currentPackageAmountOff: number | null;
  renewalPackageAmountOff: number | null;
  currentPackageDiscountCodesId: number;
  currentPackagePromoCode: string | null;
  renewalPackagePromoCode: string | null;
  promoCode: string | null;
  fromDate: string | null;
  toDate: string | null;
  renewalDate: string | null;
  bFreeTrial: boolean;
  trialRemainingDays: number | null;
  bUpgradeable: boolean;
  bRenewable: boolean;
  bAutoRenew: boolean;
  isLoading: boolean; 
  bMarkSubscriptionAsPaid: boolean;
};

export type SubscriptionsList = ISubscription[];

export interface UpgradePackageInfo {
  packageId: number;
  subjectId: number;
  price: number;
  subscription: string;
  accessLength: number;
  type: string;
}

export interface RenewSubscriptionFormValues {
  poNumber: string;
  paymentType: PaymentTypes.PAY_BY_CARD;
  cardholderName: string;
  card: string;
  validPromoCode: string;
  total: number;
  validDiscount: number;
  subOption: number[];
  FinanceEmail: string;
}

export interface RenewSubscriptionFormViaInvoiceValues {
  poNumber: string;
  paymentType: PaymentTypes.PAY_BY_INVOICE;
  validPromoCode: string;
  total: number;
  validDiscount: number;
  subOption: number[];
  FinanceEmail: string;
}

export interface accessInformation {
  bCanAddFreeTrial: boolean;
  bCanAddSubscription: boolean;
}

export class SubscriptionsParser {
  public static parseGetSubscriptionList = (response: SubscriptionsList) => {
    return response.map((s: ISubscription) => ({
      ...s,
      fromDate: s.fromDate
        ? format(parse(s.fromDate, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
        : '',
      toDate: s.toDate
        ? format(parse(s.toDate, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
        : '',

      renewalDate: s.renewalDate
        ? format(parse(s.renewalDate, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
        : '',
    }));
  };

  public static createEmptyRenewPackages: (
    SubOption: number[],
    FinanceEmail?: string | undefined
  ) => RenewSubscriptionFormValues = (SubOption, FinanceEmail) => ({
    poNumber: '',
    paymentType: PaymentTypes.PAY_BY_CARD,
    cardholderName: '',
    card: '',
    validPromoCode: '',
    total: 0,
    validDiscount: 0,
    subOption: SubOption,
    FinanceEmail: FinanceEmail ? FinanceEmail : '',
  });

  public static createEmptyRenewPackagesViaInvoice: (
    SubOption: number[],
    FinanceEmail?: string | undefined
  ) => RenewSubscriptionFormViaInvoiceValues = (SubOption, FinanceEmail) => ({
    poNumber: '',
    paymentType: PaymentTypes.PAY_BY_INVOICE,
    validPromoCode: '',
    total: 0,
    validDiscount: 0,
    subOption: SubOption,
    FinanceEmail: FinanceEmail ? FinanceEmail : '',
  });  

  public static buildRenewByInvoicePayload = (
    values: RenewSubscriptionFormValues,
    packagePromos: PackagePromo[],
    expectedGrossAmount: number
  ) => ({
    packagePromos,
    expectedGrossAmount,
    poNumber: values.poNumber,
    FinanceEmail: values.FinanceEmail,
  });

  public static parseError = (errors: any) => {
    let error: string;
    switch (errors?.[0]?.messageCode) {
      case 'Invalid_PromoCode_Expired':
        error = 'Promo code is expired';
        break;
      case 'Invalid_PromoCode_NotStarted':
        error = 'Promo code is not yet active.';
        break;
      case 'Invalid_PromoCode_Expires':
        error = "This code expires before your renewal date, so it can't be applied to your upcoming renewal.";
        break;
      case 'payment_amount_mismatch':
        error = 'Prices have changed. Please review your order and try again.';
        break;
      case 'ExpectedGrossAmount':
        error = 'Sorry, something went wrong. Please try again. ';
        break;
      case 'pkey_general_payment_subscription_failed':
        error =
          'Something went wrong when processing the payment. Please try again.';
        break;
      case 'Max_Characters_Exceeded':
        error = 'Max characters exceeded.';
        break;
      case 'Invalid_PromoCode':
        error = 'Invalid promo code.';    
        break;
      case 'Invalid_PromoCode_UsedAlready':
        error = 'Promo code already in use.';    
        break;                        
      default:
        error = 'Sorry, something went wrong. Please try again. ';
    }
    return error;
  };
}
